/**
 * modalStack — LIFO stack of open modal IDs (ADR-095 Phase 2.4).
 *
 * Implements open-webui Modal.svelte's `isTopModal()` semantics so that
 * stacked modals (e.g., CommandPalette opened from within SettingsPanel)
 * route the Escape key to ONLY the topmost modal. Without this, both
 * modals would close on a single Escape keystroke, which is the wrong
 * keyboard UX per WCAG 2.1 SC 2.1.2.
 *
 * Module-level state is intentional — there's exactly one global modal
 * stack per browser tab. The chat-ui has no SSR concern (Vite SPA only).
 */

import { useEffect } from "react";

const stack: string[] = [];

export function pushModal(id: string): void {
  stack.push(id);
}

export function popModal(id: string): void {
  const idx = stack.lastIndexOf(id);
  if (idx >= 0) stack.splice(idx, 1);
}

export function isTopModal(id: string): boolean {
  return stack[stack.length - 1] === id;
}

export function clearModalStack(): void {
  stack.length = 0;
}

export function useModalStack(id: string, isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) return;
    pushModal(id);
    return () => popModal(id);
  }, [id, isOpen]);
}
